import { useCallback, useMemo } from "react";
import { useQueries, type UseQueryResult } from "@tanstack/react-query";
import {
  fetchModelPrediction,
  studyKeys,
  type ModelPredictionResponse,
  type ReaderStudyItem,
} from "@/hooks/useStudyQueries";
import { summarizeTriageResult, type TriageWorklistSummary } from "@/types/triageReview";
import { readerCaseId } from "@/utils/readerCaseId";
import { isModelFailed, isModelRunning } from "@/utils/inferenceStatus";

/** Per-row flags for released triage-device rows (`triage_device` on the reader row). */
export function useTriageWorklist(
  token: string | null,
  userId: string | null,
  studies: readonly ReaderStudyItem[] | undefined,
): Record<string, TriageWorklistSummary> {
  const triageStudies = useMemo(() => studies?.filter((s) => s.triage_device) ?? [], [studies]);
  const combine = useCallback(
    (results: UseQueryResult<ModelPredictionResponse | null>[]) =>
      Object.fromEntries(
        triageStudies.map((study, index) => {
          const query = results[index];
          let summary: TriageWorklistSummary;
          if (isModelRunning(study.status)) {
            summary = { label: "Flags pending", findings: [], count: null, flagged: false };
          } else if (isModelFailed(study.status)) {
            summary = { label: "Prediction errored", findings: [], count: null, flagged: false };
          } else if (query.isPending) {
            summary = { label: "Results loading", findings: [], count: null, flagged: false };
          } else {
            summary = summarizeTriageResult(query.isError ? undefined : query.data?.triage_review);
          }
          return [readerCaseId(study), summary];
        }),
      ),
    [triageStudies],
  );
  return useQueries({
    queries: triageStudies.map((study) => ({
      queryKey: studyKeys.studyModelPrediction(userId || "", readerCaseId(study)),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        fetchModelPrediction(token!, userId!, readerCaseId(study), signal),
      enabled: !!token && !!userId && !isModelRunning(study.status) && !isModelFailed(study.status),
      staleTime: Infinity,
      gcTime: 1000 * 60 * 60,
    })),
    combine,
  });
}
